import { create } from 'zustand';
import { CodeEditorTheme, Theme } from '@app/types';
// This import cannot be shortened without creating circular dependencies.
import { getLocalStorage, setLocalStorage } from '@app/utilities/functions/local-storage';

const userSettingsKey = 'userSettings';

type UserSettings = {
  /** Theme for the @see CodeEditor. */
  codeEditorTheme: CodeEditorTheme;
  /** Whether the user prefers paramter values to be expanded in the @see CodeEditor or not. */
  expandedInputs: boolean;
  /** Whether save should occasionally yield to main (slower, but freezes the UI less). */
  saveShouldYield: boolean;
  /** User's preferred theme for D2. */
  theme: Theme;
};

type UserSettingsActions = {
  setCodeEditorTheme: (codeEditorTheme: CodeEditorTheme) => void;
  setSaveShouldYield: (saveShouldYield: boolean) => void;
  setTheme: (theme: Theme) => void;
  toggleExpandedInputs: () => void;
};

export const useUserSettings = create<UserSettings & UserSettingsActions>((setState) => {
  const addThemeToDom = (theme: Theme) => {
    document.querySelector('body > #root')?.setAttribute('data-theme', theme);
  };
  // Set state & local storage.
  const set = <K extends keyof UserSettings>(
    updateOrUpdater: (
      Pick<UserSettings, K>
      | ((currentState: UserSettings) => Pick<UserSettings, K>)
    )
  ) => {
    setState((currentState) => {
      const update = (typeof updateOrUpdater !== 'function') ? updateOrUpdater : (
        updateOrUpdater(currentState)
      );
      const newState: UserSettings = { ...currentState, ...update };
      setLocalStorage<UserSettings>(userSettingsKey, newState);
      if ('theme' in update) {
        addThemeToDom(update.theme as Theme);
      }
      return newState;
    });
  };

  // Default state.
  let userSettings: UserSettings = {
    codeEditorTheme: CodeEditorTheme.githubLight,
    expandedInputs: false,
    saveShouldYield: true,
    theme: Theme.light
  };
  userSettings = {
    ...userSettings,
    ...getLocalStorage<Partial<UserSettings>>(userSettingsKey, () => {
      return {};
    })
  };

  addThemeToDom(userSettings.theme);

  return {
    ...userSettings,
    setCodeEditorTheme: (codeEditorTheme) => set({ codeEditorTheme }),
    setSaveShouldYield: (saveShouldYield) => set({ saveShouldYield }),
    setTheme: (theme) => set({ theme }),
    toggleExpandedInputs: () => {
      set((currentState) => {
        return { expandedInputs: !currentState.expandedInputs };
      });
    }
  };
});
